import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { DeliveryAttempt, Order, OrderItem, ReportArtifact } from "@probetalab/contracts";
import { get, getBlob, run } from "../lib/api.js";
import { fmtDateTime, fmtPhone, fmtTime, fullName, money } from "../lib/format.js";
import { downloadBlob, printPdf } from "../lib/print.js";
import { useBranch, useSession } from "../lib/session.js";
import { deliveryChip, deliveryItemChip, deliveryStage, orderChips, studyProgress } from "../lib/status.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Card, Chip, Empty, Loading, Money, PageHead, Segmented, Switch } from "../components/ui.js";
import { Icon } from "../components/icons.js";
import { PaySheet } from "../components/PaySheet.js";
import { WhatsAppSendSheet } from "../components/WhatsAppSend.js";
import { DateRangeFilter, dateRangeQuery, useDateRange } from "../components/DateRangeFilter.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

type DeliveryFilter = "all" | "ready" | "delivered";

/**
 * Entrega: every non-cancelled order of the chosen days, oldest first, each with its stage ("Sin resultados",
 * "En proceso n/m", "Listo para entregar", "Entregado"). Printing and WhatsApp stay behind the order's own
 * rules: validated studies only, and a balance due blocks the hand-over.
 */
export function DeliveryPage() {
  const branch = useBranch();
  const navigate = useNavigate();
  // One continuous list, delivered orders included and marked by colour (Fernanda, 2026-11-02 20:32:
  // "toda la lista sea continua y solo el nombre del paciente cambie de color cuando ya se entregue").
  const [range, setRange] = useDateRange();
  const dates = dateRangeQuery(range);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState<DeliveryFilter>("all");
  const term = q.trim();
  const orders = useQuery(
    () => get("orders", { query: { branch_id: branch.id, ...dates, ...(term ? { q: term } : {}), limit: 200 } }),
    [branch.id, dates.day, dates.from, dates.to, term],
  );
  const items: Order[] = (orders.data?.items ?? [])
    .filter((o) => o.state !== "cancelled")
    .filter((o) => stage === "all" || deliveryStage(o.state) === (stage === "ready" ? "ready" : "delivered"))
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at) || a.folio.localeCompare(b.folio));
  const loading = orders.loading && !orders.data;
  return (
    <>
      <PageHead kicker={branch.name} title="Entrega de resultados" lede="Todas las solicitudes del día, con su avance. Imprime en hoja membretada o envía el PDF por WhatsApp cuando estén validadas. Con adeudo no se entrega. Las entregadas salen en verde." />
      <div className="toolbar">
        <DateRangeFilter value={range} onChange={setRange} allowAny />
        <Segmented value={stage} onChange={setStage} options={[{ value: "all", label: "Todos" }, { value: "ready", label: "Listos" }, { value: "delivered", label: "Entregados" }]} />
        <input {...NO_AUTOFILL} type="search" className="input grow" style={{ maxWidth: 320 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o folio…" aria-label="Buscar solicitud" />
      </div>
      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Card>
          <Empty icon="send" title={stage === "all" ? "Sin solicitudes" : "Nada por aquí"}
            hint={range.preset === "any" ? "Las solicitudes registradas aparecen aquí." : "Elige otras fechas o toca «Todos los días» para buscar en fechas anteriores."} />
        </Card>
      ) : (
        <Card pad={false}>
          {items.map((o) => (
            <DeliveryRow key={o.id} order={o} onOpen={() => navigate(`/entrega/${o.id}`)} onSettled={() => void orders.reload()} />
          ))}
        </Card>
      )}
    </>
  );
}

function DeliveryRow({ order: o, onOpen, onSettled }: { order: Order; onOpen: () => void; onSettled: () => void }) {
  const [settling, setSettling] = useState(false);
  // The detail carries the patient and the studies, so the row shows each study's progress
  // (Fernanda, 2026-11-02 20:29) for the same one request it already made for the patient.
  const detail = useQuery(() => get("order", { params: { id: o.id } }), [o.id, o.state]);
  const patient = detail.data?.patient;
  const studies = (detail.data?.items ?? []).filter((i) => i.state !== "cancelled");
  return (
    <div
      role="button"
      tabIndex={0}
      className="list-row clickable"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{ minHeight: 72 }}
    >
      <span className="folio folio-col">
        {o.folio}
      </span>
      <span className="grow" style={{ flex: "0 1 300px", minWidth: 200 }}>
        <div className="primary" data-delivered={o.state === "closed" || undefined} style={o.state === "closed" ? { color: "var(--ok)" } : undefined}>
          {patient ? fullName(patient) : "…"}
        </div>
        <div className="secondary">
          {fmtTime(o.occurred_at)} · {o.delivery_channels.map((c) => ({ whatsapp: "WhatsApp", print: "Impreso", email: "Correo", portal: "Portal" })[c]).join(" y ")}
          {patient?.phone ? (
            <>
              {" · "}
              <span style={{ whiteSpace: "nowrap" }}>{fmtPhone(patient.phone)}</span>
            </>
          ) : null}
        </div>
      </span>
      {studies.length > 0 && <StudyProgress studies={studies} />}
      {[
        ...(o.has_pending_samples && o.state !== "closed" ? [{ label: "Muestra pendiente", tone: "red" as const }] : []),
        deliveryChip(o.state, studies.length > 0 ? { done: studyProgress(studies).done, total: studies.length } : undefined),
        ...(o.balance_cents > 0 ? [{ label: "Adeudo", tone: "amber" as const }] : []),
      ].map((c) => (
        <Chip key={c.label} tone={c.tone}>
          {c.label}
        </Chip>
      ))}
      {o.balance_cents > 0 ? (
        <Button
          variant="primary"
          size="sm"
          icon="cash"
          onClick={(e) => {
            e.stopPropagation();
            setSettling(true);
          }}
        >
          Liquidar {money(o.balance_cents)}
        </Button>
      ) : (
        <Money cents={o.total_cents} className="muted" />
      )}
      <Icon name="chevronRight" style={{ color: "var(--ink-3)" }} />
      <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        {settling && <PaySheet open onClose={() => setSettling(false)} order={o} settle onPaid={onSettled} />}
      </span>
    </div>
  );
}

/**
 * An order's study progress in the Entrega list, exception first: a bar with one segment per study, the
 * count of validated ones, and by name only the studies still missing (Fernanda, 2026-11-02 20:29 —
 * "que se vea cuál ya se guardó y cuál ya está validado"). Seven chips per row read as noise; the full
 * list is the «Estudios» card inside the order.
 */
function StudyProgress({ studies }: { studies: OrderItem[] }) {
  const chips = studies.map((it) => ({ it, c: deliveryItemChip(it) }));
  const p = studyProgress(studies);
  return (
    <span className="delivery-studies" data-testid="delivery-row-studies">
      <span className="dp-head">
        <span className="dp-bar" aria-hidden="true">
          {chips.map(({ it, c }) => (
            <span key={it.id} className={`dp-seg ${c.tone}`} />
          ))}
        </span>
        <span className={`dp-count${p.done === p.total ? " all" : ""}`}>{p.headline}</span>
      </span>
      {p.missing && (
        <span className="dp-missing" title={p.missing}>
          {p.missing}
        </span>
      )}
    </span>
  );
}

export function DeliveryOrderPage() {
  const { orderId = "" } = useParams();
  const branch = useBranch();
  const { can } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const order = useQuery(() => get("order", { params: { id: orderId } }), [orderId]);
  const reports = useQuery(() => get("orderReports", { params: { id: orderId } }), [orderId]);
  const [letterhead, setLetterhead] = useState(true);
  const [signature, setSignature] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** Artifact and bytes for the WhatsApp sheet, fetched before it opens so share() keeps the tap's user activation. */
  const [waSend, setWaSend] = useState<{ artifact: ReportArtifact; pdf: Blob } | null>(null);
  const [waOpen, setWaOpen] = useState(false);
  /** Bumped after each recorded delivery so the per-artifact delivery chips refetch. */
  const [deliveryVersion, setDeliveryVersion] = useState(0);
  const [settling, setSettling] = useState(false);

  const artifacts = (reports.data?.items ?? []).slice().sort((a, b) => b.rendered_at.localeCompare(a.rendered_at));
  const current = artifacts.find((a) => a.options.letterhead === letterhead && a.options.signature === signature) ?? null;
  const blocked = (order.data?.balance_cents ?? 0) > 0;
  const staffView = blocked && can("quimico");

  useEffect(() => {
    let url: string | null = null;
    setPdfUrl(null);
    // Wait for the order too: the artifact list can arrive first, and a fetch before the balance is
    // known would be refused with 402 (and surface as an error) for an order that still owes money.
    if (!current || !order.data || (blocked && !staffView)) return;
    getBlob("reportPdf", { params: { id: current.id }, query: { staff_view: staffView } })
      .then((b) => {
        url = URL.createObjectURL(b);
        setPdfUrl(url);
      })
      .catch((e) => toast.error(errorMessage(e)));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [current?.id, order.data?.id, blocked, staffView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (order.loading && !order.data) return <Loading />;
  if (!order.data) return <Empty icon="warning" title="No se encontró la solicitud" hint={order.error ?? undefined} />;
  const o = order.data;
  const validated = o.items.filter((i) => i.state === "validated" || i.state === "issued");
  const allValidated = o.items.filter((i) => i.state !== "cancelled").every((i) => i.state === "validated" || i.state === "issued");

  async function ensureArtifact(): Promise<ReportArtifact> {
    if (current) return current;
    const a = await run<"report.render", ReportArtifact>("report.render", { order_id: o.id, kind: allValidated ? "final" : "partial", order_item_ids: [], options: { letterhead, signature } }, { branch_id: branch.id });
    await reports.reload();
    return a;
  }

  async function fetchPdf(a: ReportArtifact): Promise<Blob> {
    return getBlob("reportPdf", { params: { id: a.id }, query: { staff_view: staffView } });
  }

  async function record(a: ReportArtifact, channel: DeliveryAttempt["channel"], destination: string | null) {
    await run("delivery.record", { report_artifact_id: a.id, channel, destination, notes: null }, { branch_id: branch.id });
    setDeliveryVersion((v) => v + 1);
    await order.reload();
  }

  async function doPrint() {
    setBusy("print");
    try {
      const a = await ensureArtifact();
      printPdf(await fetchPdf(a));
      if (!blocked) await record(a, "print", null);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function doDownload() {
    setBusy("download");
    try {
      const a = await ensureArtifact();
      downloadBlob(await fetchPdf(a), a.filename);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function doWhatsApp() {
    if (!o.patient.phone) return;
    setBusy("whatsapp");
    try {
      const a = await ensureArtifact();
      setWaSend({ artifact: a, pdf: await fetchPdf(a) });
      setWaOpen(true);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function doInPerson() {
    setBusy("person");
    try {
      const a = await ensureArtifact();
      await record(a, "in_person", fullName(o.patient));
      toast.success("Entrega en persona registrada.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHead
        kicker={
          <>
            <Link to={`/solicitudes/${o.id}`} style={{ color: "inherit" }}>
              {o.folio}
            </Link>{" "}
            · {o.patient.age_text} · {o.doctor_display}
          </>
        }
        title={fullName(o.patient)}
        lede={
          <span className="row wrap">
            {orderChips(o).map((c) => (
              <Chip key={c.label} tone={c.tone} large>
                {c.label}
              </Chip>
            ))}
            <span className="muted">
              {validated.length} de {o.items.filter((i) => i.state !== "cancelled").length} estudios validados
            </span>
          </span>
        }
        actions={
          <Button variant="ghost" icon="chevronLeft" onClick={() => navigate("/entrega")}>
            Volver
          </Button>
        }
      />

      {blocked && (
        <div style={{ marginBottom: 18 }}>
          <Alert tone="red">
            <strong>Adeudo de {money(o.balance_cents)}.</strong> Los resultados no se entregan ni se envían hasta liquidar.{" "}
            <Button variant="primary" size="sm" icon="cash" onClick={() => setSettling(true)} style={{ marginLeft: 6 }}>
              Liquidar {money(o.balance_cents)}
            </Button>
            {staffView ? " · Como químico puedes ver el informe, pero no entregarlo." : ""}
          </Alert>
        </div>
      )}
      {!blocked && o.has_pending_samples && (
        <div style={{ marginBottom: 18 }}>
          <Alert tone="red">
            <strong>Muestra pendiente.</strong> No se entregan resultados parciales mientras falte una muestra.
          </Alert>
        </div>
      )}
      {validated.length === 0 && (
        <div style={{ marginBottom: 18 }}>
          <Alert tone="amber">Ningún estudio está validado todavía. El informe se genera en Validación.</Alert>
        </div>
      )}

      <div className="split">
        <div className="stack-lg">
          <Card title="Estudios" pad={false}>
            {o.items
              .filter((i) => i.state !== "cancelled")
              .map((it) => {
                const c = deliveryItemChip(it);
                return (
                  <div key={it.id} className="list-row" data-testid="delivery-study">
                    <span className="grow">
                      <div className="primary">{it.study_name}</div>
                      <div className="secondary">
                        {it.study_clave}
                        {it.area_name ? ` · ${it.area_name}` : ""}
                      </div>
                    </span>
                    <Chip tone={c.tone}>{c.label}</Chip>
                  </div>
                );
              })}
          </Card>

          <Card title="Formato de impresión">
            <div className="stack">
              <Switch checked={letterhead} onChange={setLetterhead}>
                Con membrete <span className="muted small">— apágalo si imprimes en hojas que ya lo traen impreso</span>
              </Switch>
              <Switch checked={signature} onChange={setSignature}>
                Con firma digitalizada <span className="muted small">— apágala si la química firma a mano</span>
              </Switch>
              {!current && validated.length > 0 && <p className="small muted">Esta combinación aún no se ha generado; se genera al imprimir, descargar o enviar.</p>}
            </div>
          </Card>

          <Card title="Entregar">
            <div className="stack">
              <Button variant="primary" size="lg" block icon="print" busy={busy === "print"} disabled={validated.length === 0 || (blocked && !staffView) || o.has_pending_samples} onClick={() => void doPrint()}>
                Imprimir informe
              </Button>
              <Button variant="secondary" size="lg" block icon="whatsapp" busy={busy === "whatsapp"} disabled={validated.length === 0 || blocked || o.has_pending_samples || !o.patient.phone} onClick={() => void doWhatsApp()}>
                Enviar por WhatsApp {o.patient.phone ? `· ${fmtPhone(o.patient.phone)}` : "· sin celular"}
              </Button>
              <div className="grid-2">
                <Button variant="secondary" icon="download" busy={busy === "download"} disabled={validated.length === 0 || (blocked && !staffView)} onClick={() => void doDownload()}>
                  Descargar PDF
                </Button>
                <Button variant="secondary" icon="check" busy={busy === "person"} disabled={validated.length === 0 || blocked || o.has_pending_samples} onClick={() => void doInPerson()}>
                  Entregado en persona
                </Button>
              </div>
              <p className="small faint">WhatsApp: primero se abre el chat del paciente y luego se envía el PDF desde la computadora o la tableta.</p>
            </div>
          </Card>

          <Card title="Informes generados" pad={false}>
            {artifacts.length === 0 ? (
              <div className="list-row faint">Todavía no hay informes.</div>
            ) : (
              artifacts.map((a) => (
                <div key={a.id} className="list-row" style={a.id === current?.id ? { background: "var(--brand-tint)" } : undefined}>
                  <Icon name="doc" style={{ color: "var(--ink-3)" }} />
                  <span className="grow">
                    <div className="primary">
                      {a.kind === "final" ? "Informe final" : a.kind === "partial" ? "Informe parcial" : "Corrección"} <span className="muted small">· {a.filename}</span>
                    </div>
                    <div className="secondary">
                      {fmtDateTime(a.rendered_at)} · {a.options.letterhead ? "con membrete" : "sin membrete"} · {a.options.signature ? "con firma" : "sin firma"}
                    </div>
                  </span>
                  <Deliveries artifactId={a.id} version={deliveryVersion} />
                </div>
              ))
            )}
          </Card>
        </div>
        <div className="sticky">
          <Card title="Informe" pad={false}>
            {pdfUrl ? <iframe title="Informe" src={`${pdfUrl}#toolbar=0&view=FitH`} className="pdf-frame" /> : <div className="loading muted small">{current ? "Cargando…" : "Elige el formato y genera el informe."}</div>}
          </Card>
        </div>
      </div>
      <PaySheet
        open={settling}
        onClose={() => setSettling(false)}
        order={o}
        settle
        onPaid={(next) => {
          order.set((prev) => (prev ? { ...prev, ...next } : prev));
          void order.reload();
        }}
      />
      {waSend && o.patient.phone && (
        <WhatsAppSendSheet
          open={waOpen}
          onClose={() => setWaOpen(false)}
          firstName={o.patient.first_name}
          phone={o.patient.phone}
          folio={o.folio}
          pdf={waSend.pdf}
          filename={waSend.artifact.filename}
          onDelivered={() => record(waSend.artifact, "whatsapp", o.patient.phone)}
        />
      )}
    </>
  );
}

function Deliveries({ artifactId, version }: { artifactId: string; version: number }) {
  const d = useQuery(() => get("reportDeliveries", { params: { id: artifactId } }), [artifactId, version]);
  const n = d.data?.items.length ?? 0;
  if (!n) return <span className="faint small">Sin entregas</span>;
  const label: Record<string, string> = { whatsapp: "WhatsApp", print: "Impreso", in_person: "En persona", email: "Correo", portal: "Portal" };
  return (
    <span className="row wrap" style={{ gap: 6 }}>
      {d.data!.items.map((x) => (
        <Chip key={x.id} tone="green">
          {label[x.channel] ?? x.channel} · {fmtTime(x.occurred_at)}
        </Chip>
      ))}
    </span>
  );
}
